/**
 * Garage Compare View
 * Side-by-side comparison of up to 3 tools
 */

import React, { useState, useEffect } from 'react';
import { X, Plus, RefreshCw, Check, AlertCircle, Sparkles } from 'lucide-react';
import garageAPI from '../../../services/garage/api';
import GarageLoadingScreen from '../components/GarageLoadingScreen';
import TourGuide from '../components/TourGuide';
import { COMPARE_TOUR_STEPS } from '../tours/compareTour';

const GarageCompareView = ({
  initialToolIds = [],
  userEmail,
  showOnboarding = false,
  onboardingChecked = true,
}) => {
  const [compareIds, setCompareIds] = useState(initialToolIds.slice(0, 3));
  const [tools, setTools] = useState([]);
  const [toolStats, setToolStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [swapModalOpen, setSwapModalOpen] = useState(false);
  const [swapSlot, setSwapSlot] = useState(0);
  const [availableTools, setAvailableTools] = useState([]);
  const [showTour, setShowTour] = useState(false);

  useEffect(() => {
    if (!onboardingChecked || showOnboarding) {
      setShowTour(false);
      return;
    }
    const tourDone = localStorage.getItem('tour-completed-compare') === 'true';
    setShowTour(!tourDone);
  }, [onboardingChecked, showOnboarding]);

  // Load tools and their stats
  useEffect(() => {
    loadTools();
  }, [compareIds]);

  const loadTools = async () => {
    setLoading(true);
    try {
      // Load all tools
      const allTools = await garageAPI.getTools();
      
      // Get the specific tools for comparison
      const selectedTools = compareIds
        .map(id => allTools.find(t => t.id === id))
        .filter(Boolean);
      
      setTools(selectedTools);

      // Load stats for each tool
      const stats = {};
      for (const tool of selectedTools) {
        try {
          const toolStats = await garageAPI.getToolStats(tool.id);
          stats[tool.id] = toolStats;
        } catch (error) {
          console.error(`Failed to load stats for ${tool.id}:`, error);
          stats[tool.id] = {
            sessions7d: 0,
            sessionsTotal: 0,
            rating: 0,
            ratingCount: 0
          };
        }
      }
      setToolStats(stats);
      setAvailableTools(allTools);
    } catch (error) {
      console.error('Failed to load tools:', error);
    } finally {
      setLoading(false);
    }
  };

  const openSwap = (slotIndex) => {
    setSwapSlot(slotIndex);
    setSwapModalOpen(true);
  };

  const pickTool = (toolId) => {
    const newCompareIds = [...compareIds];
    if (swapSlot >= newCompareIds.length) {
      newCompareIds.push(toolId);
    } else {
      newCompareIds[swapSlot] = toolId;
    }
    setCompareIds(newCompareIds);
    setSwapModalOpen(false);
  };

  const removeTool = (index) => {
    if (compareIds.length > 1) {
      const newCompareIds = compareIds.filter((_, i) => i !== index);
      setCompareIds(newCompareIds);
    }
  };

  const addToolSlot = () => {
    if (compareIds.length < 3) {
      openSwap(compareIds.length);
    }
  };

  // Calculate winners
  const maxSessions = Math.max(...tools.map(t => toolStats[t.id]?.sessions7d || 0));
  const maxRating = Math.max(...tools.map(t => toolStats[t.id]?.rating || 0));

  // Calculate verdict
  const calculateVerdict = () => {
    if (tools.length < 2) return null;

    const tool1 = tools[0];
    const tool2 = tools[1];

    const capabilityOverlap = tool1.capabilities?.filter(c => 
      tool2.capabilities?.includes(c)
    ) || [];
    
    const journeyOverlap = tool1.journeys?.filter(j => 
      tool2.journeys?.includes(j)
    ) || [];

    if (capabilityOverlap.length > 0 && journeyOverlap.length > 0) {
      const winner = (toolStats[tool1.id]?.sessions7d || 0) > (toolStats[tool2.id]?.sessions7d || 0) 
        ? tool1 : tool2;
      return {
        type: 'overlap',
        message: `These overlap — pick one.`,
        detail: `Both ${tool1.title} and ${tool2.title} target similar stages and capabilities. ${winner.title} is more battle-tested with ${toolStats[winner.id]?.sessions7d} sessions this week. Try that first; switch if it doesn't fit.`
      };
    } else {
      return {
        type: 'complement',
        message: `These complement — use both.`,
        detail: `${tool1.title} for ${tool1.capabilities?.join(' / ')}, then ${tool2.title} for ${tool2.capabilities?.filter(c => !capabilityOverlap.includes(c)).join(' / ') || tool2.capabilities?.join(' / ')}. A common workflow is to start with research, then move to analysis or generation.`
      };
    }
  };

  const verdict = calculateVerdict();

  if (loading) {
    return (
      <div className="garage-content">
        <GarageLoadingScreen message="Loading comparison…" />
      </div>
    );
  }

  return (
    <div className="garage-content">
      {/* Header */}
      <div className="garage-page-hero" style={{ marginBottom: '32px' }}>
        <div>
          <h1 className="garage-page-title">Compare <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic' }}>tools</span></h1>
          <p className="garage-page-subtitle" style={{ marginTop: '8px' }}>Side-by-side so you can pick the right one — or know how they pair.</p>
        </div>
        <div className="garage-hero-actions">
          <button
            type="button"
            className="garage-btn garage-btn-secondary"
            onClick={() => setShowTour(true)}
            title="Take a quick tour"
          >
            <Sparkles size={16} />
            Help
          </button>
        </div>
      </div>

      {/* Verdict Box */}
      {verdict && (
        <div style={{
          background: verdict.type === 'overlap' ? 'var(--warning-bg)' : 'var(--success-bg)',
          border: `1px solid ${verdict.type === 'overlap' ? 'var(--warning-border)' : 'var(--success-border)'}`,
          borderRadius: '12px',
          padding: '20px 24px',
          marginBottom: '28px'
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            {verdict.type === 'overlap' ? (
              <AlertCircle size={20} style={{ color: 'var(--warning)', marginTop: '2px', flexShrink: 0 }} />
            ) : (
              <Check size={20} style={{ color: 'var(--success)', marginTop: '2px', flexShrink: 0 }} />
            )}
            <div style={{ flex: 1 }}>
              <div style={{
                fontWeight: 600,
                fontSize: '15px',
                color: verdict.type === 'overlap' ? 'var(--warning-text)' : 'var(--success-text)',
                marginBottom: '6px'
              }}>
                {verdict.message}
              </div>
              <div style={{
                fontSize: '14px',
                color: verdict.type === 'overlap' ? 'var(--warning-text)' : 'var(--success-text)',
                opacity: 0.9,
                lineHeight: 1.5
              }}>
                {verdict.detail}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Comparison Table */}
      <div className="garage-compare-container" data-tour="compare-columns">
        {tools.map((tool, index) => (
          <div key={tool.id} className="garage-compare-column">
            {/* Tool Header */}
            <div className="garage-compare-header">
              {/* Tool Image */}
              {tool.imageUrl ? (
                <div className="garage-compare-tool-image">
                  <img src={tool.imageUrl} alt={tool.title} />
                </div>
              ) : (
                <div className="garage-compare-tool-image-placeholder">
                  <span>{tool.toolType === 'Gem' ? '✨' : '📱'}</span>
                </div>
              )}

              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  marginBottom: '4px',
                  color: 'var(--text-1)'
                }}>
                  {tool.title}
                </h3>
                <div style={{ fontSize: '13px', color: 'var(--text-3)' }}>
                  {tool.toolType}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px' }} data-tour={index === 0 ? 'compare-swap-or-add' : undefined}>
                <button
                  className="garage-icon-btn"
                  onClick={() => openSwap(index)}
                  title="Switch tool"
                >
                  <RefreshCw size={14} />
                </button>
                {compareIds.length > 1 && (
                  <button
                    className="garage-icon-btn"
                    onClick={() => removeTool(index)}
                    title="Remove"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>

            {/* Tool Content */}
            <div className="garage-compare-content">
              {/* Description */}
              <div className="garage-compare-row">
                <div className="garage-compare-label">Description</div>
                <div className="garage-compare-value">{tool.description}</div>
              </div>

              {/* Personas */}
              <div className="garage-compare-row">
                <div className="garage-compare-label">For Roles</div>
                <div className="garage-compare-value">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {tool.personas?.map(p => (
                      <span key={p} className="garage-pill garage-pill-sm">{p}</span>
                    )) || <span style={{ color: 'var(--text-3)' }}>–</span>}
                  </div>
                </div>
              </div>

              {/* Sales Stages */}
              <div className="garage-compare-row">
                <div className="garage-compare-label">Sales Stages</div>
                <div className="garage-compare-value">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {tool.journeys?.map(j => (
                      <span key={j} className="garage-pill garage-pill-sm">{j}</span>
                    )) || <span style={{ color: 'var(--text-3)' }}>–</span>}
                  </div>
                </div>
              </div>

              {/* Capabilities */}
              <div className="garage-compare-row">
                <div className="garage-compare-label">Capabilities</div>
                <div className="garage-compare-value">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {tool.capabilities?.map(c => (
                      <span key={c} className="garage-pill garage-pill-sm">{c}</span>
                    )) || <span style={{ color: 'var(--text-3)' }}>–</span>}
                  </div>
                </div>
              </div>

              {/* Environment */}
              {tool.environment && (
                <div className="garage-compare-row">
                  <div className="garage-compare-label">Environment</div>
                  <div className="garage-compare-value">
                    <span className="garage-pill garage-pill-sm">{tool.environment}</span>
                  </div>
                </div>
              )}

              {/* Makers / Created By */}
              {tool.makers && tool.makers.length > 0 && (
                <div className="garage-compare-row">
                  <div className="garage-compare-label">Created By</div>
                  <div className="garage-compare-value">
                    {tool.makers.map(m => m.displayName || m.makerEmail).join(', ')}
                  </div>
                </div>
              )}

              {/* Tags */}
              {tool.tags && tool.tags.length > 0 && (
                <div className="garage-compare-row">
                  <div className="garage-compare-label">Tags</div>
                  <div className="garage-compare-value">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {tool.tags.map(tag => (
                        <span key={tag} className="garage-pill garage-pill-sm">{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Divider */}
              <div
                style={{
                  borderTop: '1px solid var(--border-2)',
                  margin: '16px 0',
                  paddingTop: '16px'
                }}
                data-tour={index === 0 ? 'compare-usage' : undefined}
              >
                <div className="garage-compare-label" style={{ marginBottom: '12px' }}>Usage & Quality</div>
              </div>

              {/* 7-day Sessions */}
              <div className="garage-compare-row">
                <div className="garage-compare-label">Sessions (7d)</div>
                <div className="garage-compare-value">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: 'var(--text-1)'
                    }}>
                      {toolStats[tool.id]?.sessions7d || 0}
                    </span>
                    {toolStats[tool.id]?.sessions7d === maxSessions && maxSessions > 0 && (
                      <span className="garage-badge garage-badge-success">Most active</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Total Sessions */}
              <div className="garage-compare-row">
                <div className="garage-compare-label">Total Sessions</div>
                <div className="garage-compare-value">
                  {toolStats[tool.id]?.sessionsTotal?.toLocaleString() || 0}
                </div>
              </div>

              {/* Rating */}
              <div className="garage-compare-row">
                <div className="garage-compare-label">Rating</div>
                <div className="garage-compare-value">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-1)' }}>
                      {toolStats[tool.id]?.rating?.toFixed(1) || '–'}
                    </span>
                    {toolStats[tool.id]?.ratingCount > 0 && (
                      <span style={{ fontSize: '13px', color: 'var(--text-3)' }}>
                        ({toolStats[tool.id].ratingCount} {toolStats[tool.id].ratingCount === 1 ? 'review' : 'reviews'})
                      </span>
                    )}
                    {toolStats[tool.id]?.rating === maxRating && maxRating > 0 && (
                      <span className="garage-badge garage-badge-success">Top rated</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Last Updated */}
              {toolStats[tool.id]?.lastUpdated && (
                <div className="garage-compare-row">
                  <div className="garage-compare-label">Last Updated</div>
                  <div className="garage-compare-value">
                    {new Date(toolStats[tool.id].lastUpdated).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                </div>
              )}

              {/* Launch Button */}
              <div style={{ marginTop: '20px' }}>
                <button
                  className="garage-btn garage-btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => {
                    if (tool.url) {
                      window.open(tool.url, '_blank');
                      if (userEmail) {
                        garageAPI.logUsage(tool.id, userEmail);
                      }
                    }
                  }}
                >
                  Launch {tool.title}
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Add Tool Column */}
        {compareIds.length < 3 && (
          <div
            className="garage-compare-column garage-compare-add"
            data-tour={tools.length === 0 ? 'compare-swap-or-add' : undefined}
          >
            <button
              className="garage-compare-add-btn"
              onClick={addToolSlot}
            >
              <Plus size={24} />
              <span>Add tool to compare</span>
            </button>
          </div>
        )}
      </div>

      {/* Swap Tool Modal */}
      {swapModalOpen && (
        <div className="garage-modal-overlay" onClick={() => setSwapModalOpen(false)}>
          <div className="garage-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="garage-modal-header">
              <h2>Select Tool</h2>
              <button className="garage-icon-btn" onClick={() => setSwapModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="garage-modal-body">
              <div style={{ display: 'grid', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                {availableTools
                  .filter(t => !compareIds.includes(t.id))
                  .map(tool => (
                    <div
                      key={tool.id}
                      className="garage-tool-select-item"
                      onClick={() => pickTool(tool.id)}
                    >
                      {/* Tool Image */}
                      {tool.imageUrl ? (
                        <div className="garage-tool-select-image">
                          <img src={tool.imageUrl} alt={tool.title} />
                        </div>
                      ) : (
                        <div className="garage-tool-select-image-placeholder">
                          <span>{tool.toolType === 'Gem' ? '✨' : '📱'}</span>
                        </div>
                      )}

                      <div className="garage-tool-select-content">
                        <div className="garage-tool-select-header">
                          <div style={{ fontWeight: 600 }}>{tool.title}</div>
                          <span className={`garage-tool-select-badge ${tool.toolType === 'Gem' ? 'is-gem' : 'is-app'}`}>
                            {tool.toolType}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-3)', marginTop: '4px' }}>
                          {tool.description}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {showTour && !showOnboarding && !loading && (
        <TourGuide
          steps={COMPARE_TOUR_STEPS}
          storageKey="tour-completed-compare"
          onComplete={() => setShowTour(false)}
          onSkip={() => setShowTour(false)}
        />
      )}
    </div>
  );
};

export default GarageCompareView;
